import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useClickOutside } from '../hooks/useClickOutside.ts';

export type WorkPeriodValue = {
  startDate: string;
  endDate: string;
  isCurrent: boolean;
};

type WorkPeriodPickerProps = {
  value: WorkPeriodValue;
  onChange: (value: WorkPeriodValue) => void;
  id?: string;
};

function splitMonthValue(value: string): { month: number; year: string } {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) {
    return { month: 0, year: '' };
  }
  const [year, monthPart] = value.split('-');
  return { month: parseInt(monthPart, 10), year };
}

function joinMonthValue(month: number, year: string): string {
  const y = year.trim();
  if (!month || !/^\d{4}$/.test(y)) return '';
  return `${y}-${String(month).padStart(2, '0')}`;
}

function monthLabel(month: number, lang: string): string {
  const name = new Date(2000, month - 1, 1).toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US', {
    month: 'long',
  });
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function formatWorkPeriodDisplay(
  value: WorkPeriodValue,
  lang: string,
  presentLabel: string
): string {
  const { startDate, endDate, isCurrent } = value;
  if (!startDate) return '';
  const start = `${monthLabel(splitMonthValue(startDate).month, lang)} ${splitMonthValue(startDate).year}`;
  if (isCurrent) return `${start} — ${presentLabel}`;
  if (!endDate) return start;
  const end = `${monthLabel(splitMonthValue(endDate).month, lang)} ${splitMonthValue(endDate).year}`;
  return `${start} — ${end}`;
}

function MonthYearPicker({
  label,
  month,
  year,
  disabled,
  onMonthChange,
  onYearChange,
  lang,
}: {
  label: string;
  month: number;
  year: string;
  disabled?: boolean;
  onMonthChange: (month: number) => void;
  onYearChange: (year: string) => void;
  lang: string;
}) {
  return (
    <div className={`settings-work-period-section${disabled ? ' is-disabled' : ''}`}>
      <span className="settings-work-period-section__label">{label}</span>
      <ul className="settings-work-period-months" role="listbox" aria-label={label}>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
          <li key={m}>
            <button
              type="button"
              className={`settings-work-period-month${month === m ? ' is-selected' : ''}`}
              onClick={() => !disabled && onMonthChange(m)}
              disabled={disabled}
              role="option"
              aria-selected={month === m}
            >
              <span className="settings-work-period-month__radio" aria-hidden />
              <span>{monthLabel(m, lang)}</span>
            </button>
          </li>
        ))}
      </ul>
      <input
        type="text"
        inputMode="numeric"
        className="settings-input settings-work-period-year"
        value={year}
        onChange={(e) => onYearChange(e.target.value.replace(/\D/g, '').slice(0, 4))}
        placeholder={lang === 'ru' ? 'Год' : 'Year'}
        disabled={disabled}
        maxLength={4}
      />
    </div>
  );
}

export function WorkPeriodPicker({ value, onChange, id }: WorkPeriodPickerProps) {
  const { t, currentLanguage } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const startParts = splitMonthValue(value.startDate);
  const endParts = splitMonthValue(value.endDate);

  const [draftStartMonth, setDraftStartMonth] = useState(startParts.month);
  const [draftStartYear, setDraftStartYear] = useState(startParts.year);
  const [draftEndMonth, setDraftEndMonth] = useState(endParts.month);
  const [draftEndYear, setDraftEndYear] = useState(endParts.year);
  const [draftIsCurrent, setDraftIsCurrent] = useState(value.isCurrent);

  useEffect(() => {
    if (!open) return;
    const start = splitMonthValue(value.startDate);
    const end = splitMonthValue(value.endDate);
    setDraftStartMonth(start.month);
    setDraftStartYear(start.year);
    setDraftEndMonth(end.month);
    setDraftEndYear(end.year);
    setDraftIsCurrent(value.isCurrent);
  }, [open, value]);

  const displayText = useMemo(
    () => formatWorkPeriodDisplay(value, currentLanguage, t('settings.workPeriodPresent')),
    [value, currentLanguage, t]
  );

  const applyDraft = useCallback(() => {
    onChange({
      startDate: joinMonthValue(draftStartMonth, draftStartYear),
      endDate: draftIsCurrent ? '' : joinMonthValue(draftEndMonth, draftEndYear),
      isCurrent: draftIsCurrent,
    });
  }, [draftStartMonth, draftStartYear, draftEndMonth, draftEndYear, draftIsCurrent, onChange]);

  const closePicker = useCallback(() => {
    applyDraft();
    setOpen(false);
  }, [applyDraft]);

  useClickOutside(rootRef, () => {
    if (open) closePicker();
  });

  const handleToggle = () => {
    if (open) closePicker();
    else setOpen(true);
  };

  const handleCurrentChange = (checked: boolean) => {
    setDraftIsCurrent(checked);
    if (checked) {
      setDraftEndMonth(0);
      setDraftEndYear('');
    }
  };

  return (
    <div className="settings-work-period" ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`settings-input settings-work-period-trigger${open ? ' is-open' : ''}${displayText ? ' has-value' : ''}`}
        onClick={handleToggle}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {displayText || t('settings.workPeriodPlaceholder')}
      </button>
      {open && (
        <div className="settings-work-period-popover" role="dialog" aria-label={t('settings.workPeriod')}>
          <MonthYearPicker
            label={t('settings.workPeriodStart')}
            month={draftStartMonth}
            year={draftStartYear}
            onMonthChange={setDraftStartMonth}
            onYearChange={setDraftStartYear}
            lang={currentLanguage}
          />
          <div className="settings-work-period-divider" />
          <label className="settings-work-period-current">
            <input
              type="checkbox"
              checked={draftIsCurrent}
              onChange={(e) => handleCurrentChange(e.target.checked)}
            />
            <span>{t('settings.workCurrentlyLabel')}</span>
          </label>
          <MonthYearPicker
            label={t('settings.workPeriodEnd')}
            month={draftEndMonth}
            year={draftEndYear}
            disabled={draftIsCurrent}
            onMonthChange={setDraftEndMonth}
            onYearChange={setDraftEndYear}
            lang={currentLanguage}
          />
        </div>
      )}
    </div>
  );
}
